import { apiFileRemove, apiFileUpload, apiFolderCreate, apiFolderIndex, apiFolderRemove, FileItemProps, FileMineType, FolderItemProps } from "apis";
import { apiFileIndex } from "apis/file/fetch";
import { AdminBreadcrumb, AppAlert } from "components";
import useAppSelector from "hooks/useAppSelector";
import React, { useState, useEffect, Fragment, useRef, FC, RefObject } from "react";
import { ContextMenuData, ContextMenuItem, getAcceptFromFileMineType, LibraryViewMode, SelectedItemType } from ".";
import ItemFolder from "./ItemFolder";
import ItemFile from "./ItemFile";
import FormFolder from "./FormFolder";
import AppForm from "components/AppForm";
import useAppForm from "hooks/useAppForm";

interface ContentProps {
    allowSelectFolder?: boolean;
    allowSelectFile?: boolean;
    multiple?: boolean;

    onDoubleClickFile?: (item: FileItemProps) => void;
    handleSelected?: (items: SelectedItemType[]) => void;
    minType?: FileMineType[];
}
const Content: FC<ContentProps> = ({
    allowSelectFolder = true,
    allowSelectFile = true,
    multiple = true,
    onDoubleClickFile,
    handleSelected,
    minType = [
        "image",
        "word",
        "excel",
        "pdf",
        "zip",
        "video",
        "audio"
    ]
}) => {
    const [contextMenu, setContextMenu] = useState<ContextMenuData>(null);
    const [viewMode, setViewMode] = useState<LibraryViewMode>("grid");
    const [folderId, setFolderId] = useState<number>(-1);

    const [selecteds, setSelecteds] = useState<SelectedItemType[]>([]);

    const ref = useRef<HTMLUListElement>(null);
    const onContextMenu = (e: React.MouseEvent, ref: RefObject<HTMLUListElement | null>, item?: ContextMenuItem) => {
        e.preventDefault();
        e.stopPropagation();

        const clickX = e.clientX;
        const clickY = e.clientY;
        const screenW = window.innerWidth;
        const screenH = window.innerHeight;

        let menuW = 0;
        let menuH = 0;

        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            menuW = rect.width;
            menuH = rect.height;
        } else {
            menuW = 160; // fallback
            menuH = 200;
        }

        let x = clickX;
        let y = clickY;

        // Né tràn ngang phải
        if (clickX + menuW > screenW) {
            x = screenW - menuW - 5;
        }

        // Né tràn xuống dưới
        if (clickY + menuH > screenH) {
            if (clickY - menuH > 0) {
                y = clickY - menuH;
            } else {
                y = Math.max(5, screenH - menuH - 5);
            }
        }

        setContextMenu({ x, y, item });
    };
    const onContextMenuClose = () => {
        setContextMenu(null);
    };
    const onDoubleClick = (item: FolderItemProps) => {
        setFolderId(item.id);
    }

    useEffect(() => {
        window.addEventListener("click", onContextMenuClose);
        return () => {
            window.removeEventListener("click", onContextMenuClose);
        };
    }, [onContextMenuClose]);

    useEffect(() => {
        const onKeyPress = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                if (contextMenu) {
                    e.stopPropagation(); // chặn bubble
                    e.preventDefault();  // tránh hành vi mặc định
                    setContextMenu(null);
                }
            }
        };
        document.addEventListener("keydown", onKeyPress, true);
        return () => {
            document.removeEventListener("keydown", onKeyPress, true);
        };
    }, [contextMenu])

    const formSearch = useAppForm<{ q?: string; type?: FileMineType }>({ type: minType[0] || "all" });
    useEffect(() => {
        apiFolderIndex({ parentId: folderId });
        setSelecteds([]);
        formSearch.reset();
    }, [folderId])

    useEffect(() => {
        apiFileIndex({ folderId, type: formSearch.values.type && minType.includes(formSearch.values.type) ? [formSearch.values.type] : minType });
    }, [folderId, formSearch.values.type])

    const folder = useAppSelector(state => state.folder);
    const file = useAppSelector(state => state.file);

    const folderSelecteds = selecteds.filter(s => s.type === "folder");
    const fileSelecteds = selecteds.filter(s => s.type === "file");
    const inputFileRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        handleSelected?.(selecteds);
    }, [selecteds, handleSelected])

    return <Fragment>
        <input
            type="file"
            ref={inputFileRef}
            hidden
            multiple
            onChange={async (e) => {
                if (e.target.files) {
                    const files: any = e.target.files;
                    await apiFileUpload({ files, folderId });
                }
                e.target.value = "";
            }}
            accept={getAcceptFromFileMineType(minType)}
        />

        <div
            className="p-4 rounded bg-white relative h-full"
            onContextMenu={(e) => onContextMenu(e, ref)}
        >
            {/* Breadcrumb */}
            <div className="flex gap-2 mb-4 " onContextMenu={(e) => e.stopPropagation()}>
                <div
                    className="flex items-center space-x-2 text-sm text-gray-600 rounded px-3 py-2 bg-gray-200 border flex-1"
                    onContextMenu={(e) => e.stopPropagation()}
                >
                    <React.Fragment key={-1}>
                        <span
                            className="cursor-pointer hover:underline"
                            onClick={() => setFolderId(-1)}
                        >
                            <i className="fa-solid fa-house-circle-check"></i>
                        </span>
                        {folder.list.breadcrum.length > 0 && <span>/</span>}
                    </React.Fragment>
                    {folder.list.breadcrum.map((item, index) => <React.Fragment key={index}>
                        <span
                            className="cursor-pointer hover:underline"
                            onClick={() => setFolderId(item.id)}
                        >
                            {item.name}
                        </span>
                        {index < folder.list.breadcrum.length - 1 && <span>/</span>}
                    </React.Fragment>)}
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => setViewMode("grid")}
                        className={`p-2 rounded ${viewMode === "grid"
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 text-gray-600"
                            }`}
                        title="Hiển thị dạng lưới"
                    >
                        <i className="fas fa-th"></i>
                    </button>
                    <button
                        onClick={() => setViewMode("list")}
                        className={`p-2 rounded ${viewMode === "list"
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 text-gray-600"
                            }`}
                        title="Hiển thị dạng danh sách"
                    >
                        <i className="fas fa-list"></i>
                    </button>
                </div>
            </div>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" onContextMenu={(e) => e.stopPropagation()}>
                <AppForm
                    isFormSearch
                    form={formSearch}
                    onSubmit={(values) => {
                    }}
                    shadow={false}
                    cols={4}
                >
                    <AppForm.Input
                        col={3}
                        placeholder="Tìm kiếm..."
                        value={formSearch.values.q}
                        onChange={(q) => formSearch.handleChange({ q })}
                    />
                    <AppForm.Select
                        placeholder="Loại file"
                        options={minType.map(t => ({ value: t, label: t }))}
                        value={formSearch.values.type}
                        onChange={(type) => formSearch.handleChange({ type: type as FileMineType })}
                    />
                </AppForm>
            </div>

            <div>

                {/* Danh sách file/folder */}
                <div className={viewMode === "grid" ? "grid grid-cols-6 gap-4" : viewMode === "list" ? "divide-y divide-gray-200" : ""}>
                    {folder.list.data
                        ?.filter(item => ((folderId === -1 && !item.parent?.id) || item.parent?.id === folderId) && (!formSearch.values.q || item.name.toLowerCase().includes(formSearch.values.q.toLowerCase())))
                        .map(item => <ItemFolder
                            viewMode={viewMode}
                            item={item}
                            onContextMenu={onContextMenu}
                            contextMenuData={contextMenu}
                            onContextMenuClose={onContextMenuClose}
                            onDoubleClick={onDoubleClick}
                            selecteds={selecteds}
                            onSelect={setSelecteds}
                            multiple={multiple}
                            allowSelect={allowSelectFolder}
                            minType={minType}
                        />)
                    }
                    {file.list.data
                        ?.filter(item => ((folderId === -1 && !item.folder?.id) || item.folder?.id === folderId)
                            && (!formSearch.values.q || formSearch.values.q === item.path || item.name.toLowerCase().includes(formSearch.values.q.toLowerCase()))
                        )
                        .map(item => <ItemFile
                            viewMode={viewMode}
                            item={item}
                            onContextMenu={onContextMenu}
                            contextMenuData={contextMenu}
                            onContextMenuClose={onContextMenuClose}
                            selecteds={selecteds}
                            onSelect={setSelecteds}
                            multiple={multiple}
                            allowSelect={allowSelectFile}

                            onDoubleClick={onDoubleClickFile}
                        />)
                    }
                </div>

                <ul
                    ref={ref}
                    className="fixed bg-white border rounded shadow-lg w-40 z-50 max-h-[calc(100vh-20px)] overflow-y-auto"
                    style={{
                        top: contextMenu?.y ?? 0,
                        left: contextMenu?.x ?? 0,
                        opacity: contextMenu && !contextMenu.item ? 1 : 0,
                        pointerEvents: contextMenu && !contextMenu.item ? "auto" : "none",
                    }}
                >
                    <li
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={(e) => {
                            const modalInstance = AppAlert.modal({
                                title: "Nhập tên thư mục",
                                content: <div className="w-[500px]">
                                    <FormFolder
                                        onClose={() => modalInstance.close()}
                                        onSubmit={async (values) => {
                                            const res = await apiFolderCreate({ ...values, parentId: folderId === -1 ? undefined : folderId });
                                            if (res.status) {
                                                modalInstance.close();
                                            }
                                        }}
                                    />
                                </div>
                            });
                        }}
                    >
                        Thêm thư mục
                    </li>
                    <li
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => inputFileRef.current?.click()}
                    >Thêm file</li>
                    <li className="px-4 py-2 hover:bg-gray-100 cursor-pointer">Dán</li>
                    <li
                        className="px-4 py-2 hover:bg-red-100 text-red-600 cursor-pointer aria-disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:hover:bg-transparent"
                        aria-disabled={folderSelecteds.length === 0 && fileSelecteds.length === 0}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (folderSelecteds.length === 0 && fileSelecteds.length === 0) {
                                return;
                            }
                            onContextMenuClose();
                            AppAlert.confirm({
                                title: "Bạn chắc chắn muốn xoá",
                                content: <div>

                                    {folderSelecteds.length > 0 && <div className="font-bold">Thư mục</div>}
                                    {folderSelecteds.map(item => <div>
                                        - {item.item.name}
                                    </div>)}
                                    {fileSelecteds.length > 0 && <div className="font-bold">Tài liệu</div>}
                                    {fileSelecteds.map(item => <div>
                                        - {item.item.name}
                                    </div>)}
                                </div>,
                                onOk: async () => {
                                    await Promise.all([
                                        folderSelecteds.length > 0 ? apiFolderRemove({ ids: folderSelecteds.map(item => item.item.id) }) : undefined,
                                        fileSelecteds.length > 0 ? apiFileRemove({ ids: fileSelecteds.map(item => item.item.id) }) : undefined
                                    ]);
                                },
                            })
                        }}
                    >
                        Xóa
                    </li>
                </ul>
            </div>
            {/* Context menu chuột phải */}
        </div>
    </Fragment>;
};

export default Content;
